import UiIcon from '@/components/ui/UiIcon';
import { useEffect, useRef, useState } from 'react';
import { fetchLinkPreview as fetchLinkPreviewClient, type LinkPreview as ClientLinkPreview } from '@/utils/linkPreviewClient';

const draftLinkPreviewCache = new Map<string, ClientLinkPreview>();

export function DraftLinkPreviewCard({
  url,
  loading,
  preview,
  onClose,
}: {
  url: string;
  loading: boolean;
  preview: ClientLinkPreview | null;
  onClose: () => void;
}) {
  if (!url) return null;
  const isVideo = (preview?.kind || 'site') === 'video';

  return (
    <div
      className={`mb-2 rounded-xl border border-site-border bg-site-text/5 overflow-hidden ${
        isVideo ? 'h-[176px]' : 'h-[68px]'
      }`}
      role="group"
      aria-label="Предпросмотр ссылки"
    >
      <div className="relative h-full">
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onClose();
          }}
          className="absolute right-2 top-2 z-10 w-8 h-8 rounded-full bg-black/35 border border-site-border text-site-text/90 hover:bg-black/45 flex items-center justify-center transition active:scale-[0.95]"
          title="Скрыть предпросмотр"
          aria-label="Скрыть предпросмотр"
        >
          ✕
        </button>

        {preview ? (
          <a
            href={preview.url}
            target="_blank"
            rel="noopener noreferrer external"
            className="block h-full hover:bg-site-text/5 transition-colors"
            onClick={(e) => e.stopPropagation()}
          >
            {isVideo ? (
              <div className="h-full flex flex-col">
                <div className="relative w-full h-[112px] bg-site-surface/60 overflow-hidden">
                  {preview.image ? (
                    <img src={preview.image} alt="" className="w-full h-full object-cover" loading="lazy" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-sm text-site-text/70">
                      {preview.domain || 'видео'}
                    </div>
                  )}
                  <div className="absolute inset-0 flex items-center justify-center bg-black/15" aria-hidden="true">
                    <span className="w-12 h-12 rounded-full bg-black/40 border border-site-border flex items-center justify-center text-site-text/95">
                      <UiIcon name="Play" />
                    </span>
                  </div>
                </div>
                <div className="px-3 py-2 flex-1 min-w-0">
                  <div className="text-sm text-site-text/90 truncate">{preview.title || preview.domain || 'Видео'}</div>
                  {preview.description ? (
                    <div className="mt-0.5 text-xs text-site-text/70 line-clamp-2">{preview.description}</div>
                  ) : null}
                  <div className="mt-0.5 text-[11px] text-site-accent/80 truncate">{preview.domain}</div>
                </div>
              </div>
            ) : (
              <div className="flex gap-3 h-full">
                <div className="relative w-16 h-16 bg-site-surface/60 flex items-center justify-center text-sm text-site-text/70 shrink-0 overflow-hidden">
                  {preview.image ? (
                    <img src={preview.image} alt="" className="w-full h-full object-cover" loading="lazy" />
                  ) : (
                    <span className="px-2 text-center break-words leading-4">{preview.domain || 'ссылка'}</span>
                  )}
                </div>
                <div className="py-2 pr-3 flex-1 min-w-0">
                  <div className="text-sm text-site-text/90 truncate">{preview.title || preview.domain || 'Ссылка'}</div>
                  {preview.description ? (
                    <div className="mt-0.5 text-xs text-site-text/70 line-clamp-2">{preview.description}</div>
                  ) : null}
                  <div className="mt-0.5 text-[11px] text-site-accent/80 truncate">{preview.domain}</div>
                </div>
              </div>
            )}
          </a>
        ) : (
          <div className="flex gap-3 h-full items-center">
            <div className="w-16 h-16 bg-site-surface/40 shrink-0 flex items-center justify-center">
              <span className="w-5 h-5 border-2 border-site-border border-t-white/60 rounded-full animate-spin" />
            </div>
            <div className="py-2 pr-3 flex-1 min-w-0">
              <div className="text-xs text-site-muted">{loading ? 'Загружаем превью…' : '—'}</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export function useDraftLinkPreview(url: string | null, enabled: boolean) {
  const [preview, setPreview] = useState<ClientLinkPreview | null>(null);
  const [loading, setLoading] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  const timeoutRef = useRef<number | null>(null);

  useEffect(() => {
    if (!enabled || !url) {
      setPreview(null);
      setLoading(false);
      abortRef.current?.abort();
      if (timeoutRef.current != null) window.clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
      return;
    }

    const cached = draftLinkPreviewCache.get(url);
    if (cached) {
      setPreview(cached);
      setLoading(false);
      return;
    }

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true);

    if (timeoutRef.current != null) window.clearTimeout(timeoutRef.current);
    timeoutRef.current = window.setTimeout(() => {
      const run = async () => {
        try {
          const previewData = await fetchLinkPreviewClient(url, controller.signal);
          if (!previewData || controller.signal.aborted) return;
          draftLinkPreviewCache.set(url, previewData);
          setPreview(previewData);
        } finally {
          if (!controller.signal.aborted) setLoading(false);
        }
      };
      void run();
    }, 250);

    return () => {
      controller.abort();
      if (timeoutRef.current != null) window.clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    };
  }, [enabled, url]);

  return { preview, loading };
}
